import React from 'react'
import { Box, Typography, Button } from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import DashboardIcon from '@mui/icons-material/Dashboard'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import CampaignIcon from '@mui/icons-material/Campaign'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import PageHeader from '../components/system/PageHeader'
import { timeOfDayGreeting } from '../utils/greeting'

/**
 * General landing page shown when a customer is selected but no job.
 * Provides navigation to cross-job features: dashboards, agent, alerts.
 * Also shows a job picker to navigate to a specific job.
 */
const GeneralLanding: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const customer = account.customer?.name || ''
  const customerDisplay = account.customer?.displayName || customer
  const greeting = timeOfDayGreeting()
  const userPermissions = account.user?.domain_permissions
  const isManager = account.user?.role_permissions?.isManager
  const jobs = account.customer?.sessionJobs || []

  if (!account.user || !customer) return null

  const crossJobLinks = [
    { label: 'DataViz', icon: <DashboardIcon />, onClick: () => router.navigate('dashboards', { customer }), visible: !!userPermissions?.canAccessDashboards },
    { label: 'Agent', icon: <SmartToyIcon />, onClick: () => router.navigate('agent', { customer }), visible: !!userPermissions?.canAccessAgent },
    // T6.1 — the chooser. This landing page has a customer but no job, so the
    // job half of the gate cannot be checked here; the chooser itself is
    // customer/job-fixed server-side, and an unentitled user gets no cards.
    { label: 'Social Media', icon: <CampaignIcon />, onClick: () => router.navigate('sky-social-media'), visible: !!userPermissions?.canAccessSocialMediaPOC },
    { label: 'Alerts', icon: <NotificationsActiveIcon />, onClick: () => router.navigate('alerts', { customer }), visible: true },
  ].filter(l => l.visible)

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title={greeting}
        subtitle={customerDisplay}
      />

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, px: 3 }}>
        {/* Cross-job features */}
        {crossJobLinks.length > 0 && (
          <Box sx={{ width: '100%', maxWidth: 600, mb: 5 }}>
            <Typography variant="subtitle2" sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: '0.75rem',
              mb: 2,
            }}>
              Quick Access
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {crossJobLinks.map(link => (
                <Button
                  key={link.label}
                  variant="outlined"
                  color="info"
                  startIcon={link.icon}
                  onClick={link.onClick}
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    py: 1.5,
                    fontSize: '0.875rem',
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {/* Job picker */}
        <Box sx={{ width: '100%', maxWidth: 600 }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
            mb: 2,
          }}>
            Select a Job
          </Typography>
          {jobs.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {jobs.map(j => (
                <Box
                  key={j.name}
                  onClick={() => account.setJobID(j.name || '')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableChartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {j.displayName || j.name}
                    </Typography>
                    {j.displayName && j.name !== j.displayName && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {j.name}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No jobs available for this customer
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default GeneralLanding
